import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  DownloadSingleReportConfig,
  managePrimaryOMSOrderRowsData,
  orderSourceConfig,
  orderStatusConfig,
} from "../../types/types";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { capitalize, capitalizeFirstLetter } from "../../utils/utils";
import { ReactComponent as DownloadAllIcon } from "../../assets/image/svg/downloadAllWhite.svg";
import {
  Alert,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
} from "@mui/material";
import {
  getOrderList,
  getUTCDate,
} from "../../services/manageOrderService";
import "./ManagePrimaryOrder.css";
import "../viewOrder/ViewOrder.css";
import "../manageBasket/ManageBasket.css";
import { Loader } from "../../components/loader/Loader";
import Header from "../../components/dms_common/Header";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import {
  getDownloadSingleReportConfig,
  getOrderSource,
} from "../secondaryOrder/ManageSecondaryOrderUtils";
import {
  filterData,
  searchWithStatusFilter,
  updateDatesInLocalStorage,
  getMultipleFilterBasedData,
} from "../../components/dms_common/DmsCommonServices";
import {
  isGapMoreThanNDays,
  isStartDateIsGreaterThanEndDate,
} from "../../utils/calenderUtils";
import { downloadAllReportsPrimarySecondary } from "../../services/manageReportService";
import { getConfigData } from "../../services/configurationService";
import {
  getButtonOnClick,
  getDisabledButtonSx,
  safeStringify,
} from "../../components/dms_common/DmsUtils";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import {
  GenericModal,
  getGenericModalProps,
  initialGenericModalProps,
} from "../viewOrder/ViewOrderUtils";
import CustomGenericModal from "../../components/dms_common/CustomGenericModal";
import useSessionStorageDates from "../../components/dms_common/UseSessionStorageDates";
import { managePrimaryOrderColumnsMetaData } from "./ManagePrimaryOrderUtils";
import { sendSlackAlert, SlackChannel } from "../../services/slackService";

const ManagePrimaryOrder = () => {
  const { storedStartDate, storedEndDate, storeSessionStorageDates } = useSessionStorageDates("manageOrders");

  const [lastRefreshed, setLastRefreshed] = useState<Dayjs>(dayjs());
  const [isProcessing, setIsProcessing] = useState(false);
  const [data, setData] = useState<managePrimaryOMSOrderRowsData[]>([]);
  const [rows, setRows] = useState<managePrimaryOMSOrderRowsData[]>([]);
  const [orderTrackingStagesList, setOrderTrackingStagesList] = useState<orderStatusConfig[]>([]);
  const [startDate, setStartDate] = useState<Dayjs>(storedStartDate);
  const [endDate, setEndDate] = useState<Dayjs>(storedEndDate);
  const [loader, setLoader] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const allOrdersReportIdRef = useRef<string | null>(null);
  const orderSourcesRef = useRef<Record<string, orderSourceConfig>>({});
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [downloadSingleReportConfig, setDownloadSingleReportConfig] = useState<DownloadSingleReportConfig | null>();
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(initialGenericModalProps);
  const [resetSearch, setResetSearch] = useState(false);
  const [showSiteId, setShowSiteId] = useState(false);
  const [showSiteStatusFilter, setShowSiteStatusFilter] = useState<boolean>(false);
  const [multipleFiltersVisible, setMultipleFiltersVisible] = useState<boolean>(false);
  const [checkedOrderNumbers, setCheckedOrderNumbers] = useState<string[]>([]);
  const [nonSelectableOrderNumbers, setNonSelectableOrderNumbers] = useState<
    string[]
  >([]);

  const [multipleFiltersList, setMultipleFiltersList] = useState<Record<string, Record<string, number>>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(() => {
    const stored = localStorage.getItem('multipleFiltersListManagePrimaryOMS');
    return stored ? JSON.parse(stored) : {};
  });
  const [filterKeys, setFilterKeys] = useState<string[]>([]); 
  const [multipleFilterStatusLabelMap, setMultipleFilterStatusLabelMap] = useState<Record<string, string>>({"status": "Status"}); 
  const [selectedFilterType, setSelectedFilterType] = useState("status");
  const [filterSearchValue, setFilterSearchValue] = useState<string>("");

  const feature = "Sale";
  const pageID = "ManageOrder";

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const validDateRange = ConfigDictionary.dateRangeConfig ?? 31;
  function canWeMakeRequest() {
    if (startDate && endDate && isGapMoreThanNDays(startDate, endDate, validDateRange)) {
      setGenericModalProps(
        getGenericModalProps(
          "positive",
          `You can't select date range more than ${validDateRange} days!`
        )
      );
      return false;
    }
    if (
      startDate &&
      endDate &&
      isStartDateIsGreaterThanEndDate(startDate, endDate)
    ) {
      setGenericModalProps(
        getGenericModalProps(
          "positive",
          "To Date should be greater than from date."
        )
      );
      return false;
    }
    updateDatesInLocalStorage(startDate, endDate, feature, pageID);
    return true;
  }

  const handleApply = async () => {
    if (canWeMakeRequest()) {
      storeSessionStorageDates(startDate, endDate);
      const start = getUTCDate(startDate, "start");
      const end = getUTCDate(endDate, "end");
      getAllOrderData(start, end);
    }
  };

  const handleClearAll = () => {
    const date = dayjs();
    storeSessionStorageDates(date, date);
    const start = getUTCDate(date, "start");
    const end = getUTCDate(date, "end");
    setStartDate(date);
    setEndDate(date);
    getAllOrderData(start, end);
    setSelectedFilters({});
  };

  useEffect(() => {
    localStorage.setItem('multipleFiltersListManagePrimaryOMS', JSON.stringify(selectedFilters));
  }, [selectedFilters]);

  const handleSelectedFilterChange = (key: string, value: string) => {
    setSelectedFilters((prev) => {
      const current = prev[key] || [];
      return {
        ...prev,
        [key]: current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value],
      };
    });
  };

  const handleSetCheckedIdArray = (ids: any[]) => {
    const updatedIds = [...ids]?.filter(
      (id: string) => !nonSelectableOrderNumbers?.includes(id)
    );
    setCheckedOrderNumbers(updatedIds);
  };

  const getAllOrderData = async (startDate: string, endDate: string) => {
    setLastRefreshed(dayjs());
    setResetSearch(true);
    setTimeout(() => setResetSearch(false), 0);

    try {
      setLoader(true);

    const response = await getOrderList(startDate, endDate, "primary");
      if (response?.data?.features?.length > 0) {
        let responseArray: managePrimaryOMSOrderRowsData[] = [];
        response?.data?.features?.forEach((item: any, index: any) => {
          const {
            id,
            outletName,
            outletCode,
            creationTime,
            totalCaseQuantity,
            totalPieceQuantity,
            totalOtherUnitQuantity,
            netAmount,
            status,
            orderNumber,
            salesRepName,
            salesRepId,
            invoiceNumber,
            eInvoice,
            areaCode,
            orderDetails,
          } = item || {};

          const extraParams = status === "INVOICED" ? `?type=invoice` : ``;
          const orderSource = getOrderSource(item);

          const isCancelledInvoice: boolean =
            status?.toLowerCase() == "rejected" && invoiceNumber ? true : false;

          const siteId = item?.extendedAttributes?.siteId ?? ""

          responseArray.push({
            id,
            areaCode,
            outletName,
            outletCode,
            creationTime,
            totalCaseQuantity,
            totalPieceQuantity,
            totalOtherUnitQuantity,
            netAmount: netAmount,
            status: status
              .toLowerCase()
              .replace(/(?:^|\s|&)\S/g, (match: any) => match?.toUpperCase()),
            orderNumber,
            source: orderSource,
            extraParams,
            salesRepName,
            salesRepId,
            checkOrderStatus: true,
            eInvoiced: eInvoice === "true",
            cancelledInvoice: isCancelledInvoice,
            orderDetails,
            siteId,
          });
        });

        setRows(responseArray);
        setData(responseArray);
        setStatusFilter("ALL");
        setSearchedValue("");

      } else {
        setRows([]);
        setData([]);
      }
    } catch (error) {
      await sendSlackAlert(
        SlackChannel.DMS_PRIMARY_UI_NOTIFICATION,
        "Error Fetching Orders - ManagePrimaryOrder",
        `Failed to fetch order data for startDate ${startDate} and endDate ${endDate} in ManagePrimaryOrder. Error: ${(error as Error).message}`,
        safeStringify({ error, startDate, endDate, file: "ManagePrimaryOrder.tsx" })
      );
      setRows([]);
      setGenericModalProps(
        getGenericModalProps("error", "Something went wrong")
      );
    } finally {
      setLoader(false);
    }
  };

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  const handleStatusFilterChange = (newFilter: string) => {
    setStatusFilter(newFilter);
  };

  const filteredData = useMemo(() => {
    const labelToIdMap = new Map<string, string>();

    orderTrackingStagesList.forEach((stage) => {
      if (stage?.dmsLabel && stage?.id) {
        labelToIdMap.set(stage.dmsLabel.toUpperCase(), stage.id);
      }
    });

    let statuses: string[] = [];
    if (filterKeys.includes("status")) {
      (selectedFilters["status"] || []).forEach((status) => {
        statuses = [...statuses, ...labelToIdMap.get(status.toUpperCase())?.split(',').map((label) => label.trim()) ?? []];
      });
    }

    const updatedSelectedFilters = { ...selectedFilters };
    if (filterKeys.includes("status")) {
      updatedSelectedFilters["status"] = statuses;
    }

    const multipleFilteredData = showSiteStatusFilter
      ? getMultipleFilterBasedData(data, updatedSelectedFilters, filterKeys)
      : data;

    const searchFilteredData = filterData(
      multipleFilteredData,
      searchedValue?.toLowerCase(),
      searchWithStatusFilter,
      statusFilter
    );

    const filteredResults = multipleFilteredData.filter((item: any) =>
      new Set(searchFilteredData).has(item)
    );


    return filteredResults;
  }, [
    searchedValue,
    statusFilter,
    data,
    selectedFilters,
    filterKeys,
  ]);

  useEffect(() => {
    setRows(filteredData);
  }, [filteredData]);


  const getInitalTableData = () => {
    const start = getUTCDate(startDate, "start");
    const end = getUTCDate(endDate, "end");
    getAllOrderData(start, end);
  };

  const handleDownloadAll = async () => {
    if (isProcessing) return;
    try{
    setIsProcessing(true);

    await downloadAllReportsPrimarySecondary(
      allOrdersReportIdRef.current,
      setSnackbarOpen,
      startDate,
      endDate,
      "type",
      "primary",
      setIsProcessing,
      downloadSingleReportConfig,
      checkedOrderNumbers,
      rows
    );

  } catch(error) {
    await sendSlackAlert(
      SlackChannel.DMS_PRIMARY_UI_NOTIFICATION,
      "Error Downloading Reports - ManagePrimaryOrder",
      `Failed to download  reports for startDate ${startDate} and endDate ${endDate} in ManagePrimaryOrder. Error: ${(error as Error).message}`,
      safeStringify({ error, startDate, endDate, allOrdersReportId: allOrdersReportIdRef.current, file: "ManagePrimaryOrder.tsx" })
    );
  }
  finally{
    setIsProcessing(false);
  }
  };

  const fetchRequiredConfigs = async () => {
    try {
    const stages = ConfigDictionary?.primaryOrderTrackingStages;
    stages.unshift({
        id: "ALL",
        color: "#F0F3FF",
        dmsLabel: "All Orders",
        labelColor: "",
      });
      const orderReportIdPConfig = await getConfigData("dms_configuration","orderReportIdPrimary");
      const orderReportIdConfig = await getConfigData("dms_configuration", "orderReportId");
      const orderSourcesConfig = await getConfigData("dms_configuration","orderSources");
      const res = await getConfigData("dms_configuration","showSiteId");
      const res1 = await getConfigData("dms_configuration","enableSiteStatusFilter");
      setShowSiteStatusFilter(res1[0]?.configValue ?? false)
      setShowSiteId(res[0]?.configValue ?? false)
      const res9 = await getConfigData("dms_configuration", "allPrimaryOrdersReportId");
      const res10 = await getConfigData("dms_configuration", "multipleFilterStatusLabelMap");
      const filterKeysConfig = await getConfigData("dms_configuration","multiFilterKeysManagePrimaryOMS");
      let defaultKeys = ["status"];
      const siteKeyValue = res[0]?.configValue ?? false
      if (siteKeyValue) {
        defaultKeys.push("siteId");
      }
      setFilterKeys(filterKeysConfig[0]?.configValue ?? defaultKeys);
      const orderReportId = orderReportIdPConfig[0]?.configValue ?? orderReportIdConfig[0]?.configValue ?? "";
      const orderSourcesMapping: Record<string, orderSourceConfig> = {};
      orderSourcesConfig[0]?.configValue?.forEach(
        (sourceObj: orderSourceConfig) => {
          orderSourcesMapping[sourceObj.id] = sourceObj;
        }
      );
      setMultipleFilterStatusLabelMap(res10[0]?.configValue ?? {"status": "status"});
      orderSourcesRef.current = orderSourcesMapping;
      allOrdersReportIdRef.current = res9[0] ? res9[0]?.configValue : orderReportId;
      setOrderTrackingStagesList(stages);
      setDownloadSingleReportConfig(getDownloadSingleReportConfig(orderReportId));
    } catch (e) {
      await sendSlackAlert(
        SlackChannel.DMS_PRIMARY_UI_NOTIFICATION,
        "Unexpected Error Fetching Configs - ManagePrimaryOrder",
        `Unexpected error while fetching configurations in ManagePrimaryOrder. Error: ${(e as Error).message}`,
        safeStringify({ e, file: "ManagePrimaryOrder.tsx" })
      );
      console.error("Error while fetching configs", e);
    }
  };

  const init = async () => {
    setLoader(true);
    await Promise.allSettled([fetchRequiredConfigs(), getInitalTableData()]);
  };

  useEffect(() => {
      init();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      getInitalTableData();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [startDate, endDate]);

  useEffect(() => {
    const counts: Record<string, Record<string, number>> = {};
    filterKeys.forEach((key) => {
      counts[key] = {};
    });

    if (filterKeys.includes("status")) {
      const labelToIdMap = new Map<string, string>();
      let allDmsLabel: string | undefined;
      orderTrackingStagesList.forEach((stage) => {
        if (stage?.dmsLabel && stage?.id) {
          labelToIdMap.set(stage.dmsLabel.toUpperCase(), stage.id);
          if (stage.id === "ALL") {
            allDmsLabel = stage.dmsLabel.toUpperCase();
          }
        }
      });

      orderTrackingStagesList.forEach((stage: any) => {
        if (stage?.dmsLabel && stage.dmsLabel.toUpperCase() !== allDmsLabel) {
          counts["status"][stage.dmsLabel.toUpperCase()] = 0;
        }
      });

      data.forEach((row: any) => {
        const rowStatus = row?.status?.toUpperCase();
        Object.keys(counts["status"]).forEach((key) => {
          const labels = labelToIdMap.get(key)?.split(',').map((label) => label.trim()) ?? [];
          if (labels.includes(rowStatus)) {
            counts["status"][key] += 1;
          }
        });
      });
    }

    filterKeys.filter((key) => key !== "status").forEach((key) => {
      data.forEach((row: any) => {
        const value = row[key];
        if ( typeof value === "string" && value) {
          counts[key][value.toLowerCase()] = (counts[key][value.toLowerCase()] || 0) + 1;
        }
      });
    });

    setMultipleFiltersList(counts);
  }, [orderTrackingStagesList, data, filterKeys]);


  const handleFilterTypeChange = (type: string) => {
    setSelectedFilterType(type);
    setFilterSearchValue("");
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };
  return (
    <div className="manage_order_parent">
      <ScreenOverlay apply={loader} />

      <CustomGenericModal
        type={genericModalProps?.type}
        message={genericModalProps?.message}
        leftButtonText={genericModalProps?.leftButtonText}
        modalOpen={genericModalProps?.open}
        onClose={() => setGenericModalProps(initialGenericModalProps)}
      />

      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>

      <Header
        title={"Manage Primary Orders"}
        searchFunction={loader ? () => {} : handleSearchValueChange}
        onRefresh={init}
        resetSearch={resetSearch}
        lastRefreshed={lastRefreshed}
      />

      <div className="dms_toolbar">
        <div className="manage_order_support_utility_dates_manageOrder">
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            className="manage_order_dateEnter"
          >
            <DatePicker
              label="From Date"
              inputFormat="DD-MM-YYYY"
              value={startDate}
              maxDate={dayjs()}
              onChange={(value) => {
                if (value) {
                  setStartDate(value);
                }
              }}
              renderInput={(props) => (
                <TextField
                  {...props}
                  inputProps={{
                    ...props.inputProps,
                    readOnly: true,
                  }}
                  disabled
                  size="small"
                />
              )}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To Date"
              inputFormat="DD-MM-YYYY"
              value={endDate}
              maxDate={dayjs()}
              onChange={(value) => {
                if (value) {
                  setEndDate(value);
                }
              }}
              renderInput={(props) => (
                <TextField
                  {...props}
                  inputProps={{
                    ...props.inputProps,
                    readOnly: true,
                  }}
                  disabled
                  size="small"
                />
              )}
            />
          </LocalizationProvider>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#237C9D",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
                backgroundColor: "#237C9D",
              },
            }}
            onClick={handleApply}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            sx={{
              textTransform: "none",
              backgroundColor: "#f0fbff",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
                backgroundColor: "#f0fbff",
              },
            }}
            onClick={handleClearAll}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            sx={{
              ...getDisabledButtonSx(snackbarOpen),
              textTransform: "none",
              minWidth: "160px",
              backgroundColor: "var(--dashboard-theme-color)",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
                background: "var(--dashboard-theme-color)",
              },
            }}
            onClick={getButtonOnClick(handleDownloadAll, snackbarOpen)}
            disableRipple={snackbarOpen}
          >
            <DownloadAllIcon style={{ marginRight: "6px" }} />
            Download All
          </Button>
        </div>
        {showSiteStatusFilter ? (
          <FormControl className="manage_secondary_orders_filter">
          <span style={{ fontSize: "14px", color: "#6E6969" }}>Filter:</span>
          <Select
            variant="outlined"
            labelId="dropdown_label"
            open={multipleFiltersVisible}
            value="Filter Orders"
            onOpen={() => {
              setMultipleFiltersVisible(true);
              setFilterSearchValue("");
            }}
            onClose={() => setMultipleFiltersVisible(false)}
            renderValue={() => (
              <span style={{ color: "#909090", fontSize: "14px" }}>
                Filter Orders
              </span>
            )}
            sx={{
              minWidth: "190px",
              marginLeft: "10px",
              "& .MuiSelect-select": {
                height: "32px",
                padding: "6px 12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#909090",
                outline: "none",
                border: "none",
              },
              "& .MuiOutlinedInput-root.Mui-focused": {
                outline: "none",
                boxShadow: "none",
              },

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#DEDEDE",
                borderWidth: "1px",
              },

              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#DEDEDE",
              },

              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#DEDEDE",
                borderWidth: "1px",
              },
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "right",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "right",
              },
              PaperProps: {
                sx: {
                  boxShadow: "none",
                  marginTop: "4px",
                  border: "1px solid #DEDEDE",
                },
              },
              MenuListProps: {
                sx: {
                  paddingTop: "8px",
                  paddingBottom: "0",
                },
              },
            }}
          >
            <MenuItem
              key={`filter-${selectedFilterType}-${JSON.stringify(selectedFilters)}`}
              disableRipple
              style={{
                padding: 0,
                backgroundColor: "transparent",
                outline: "none",
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="MSO_filter-dropdown-container"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="MSO_filter-header">
                  <span className="MSO_filter-name">All Filters</span>
                  <span
                    className={`MSO_filter-clear-all ${
                      (Object.values(selectedFilters).some((arr) => arr.length > 0) || 
                        filterSearchValue) &&
                      `MSO_filter-clear-all_active`
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFilters({});
                      setFilterSearchValue("");
                    }}
                  >
                    Clear All
                  </span>
                </div>
                <div className="MSO_filter_searchBar_container">
                  <div></div>
                  <input
                    className="MSO_fiter_searchBar"
                    placeholder="Search..."
                    value={filterSearchValue}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {setFilterSearchValue(e.target.value)
                      e.stopPropagation();
                    }}
                    autoFocus
                    onBlur={(e) => e.target.focus()}
                  />
                </div>

                <div className="MSO_filter-options">
                  <div className="MSO_filter-options-left">
                    {filterKeys.map((key) => (
                      <div
                        key={key}
                        className={`${selectedFilterType === key && `MSO_filterSelected`}`}
                        onClick={(e) => {
                          handleFilterTypeChange(key);
                          e.stopPropagation();
                        }}
                      >
                        {multipleFilterStatusLabelMap?.[key] ?? key} ({(selectedFilters[key] || []).length})
                      </div>
                    ))}
                  </div>
                  <div className="MSO_filter-options-right">
                    {filterKeys.map(
                      (key) =>
                        selectedFilterType === key && (
                          <div className="MSO_filter-option" key={key}>
                            {Object.keys(multipleFiltersList[key] ?? {})
                              .filter((value) =>
                                filterSearchValue && typeof value === "string"
                                  ? value.toLowerCase().includes(filterSearchValue.toLowerCase())
                                  : true
                              )
                              .map((value) => (
                                <label
                                  key={value}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    color: "#444444",
                                    fontSize: "13px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    checked={(selectedFilters[key] || []).includes(value)}
                                    onChange={() => handleSelectedFilterChange(key, value)}
                                    style={{ borderColor: "#CBCBCB" }}
                                  />
                                   {`${capitalizeFirstLetter(value)} (${multipleFiltersList[key]?.[value] || 0})`}
                                </label>
                              ))}
                          </div>
                        )
                    )}
                  </div>
                </div>
              </div>
            </MenuItem>
          </Select>
        </FormControl>
        ) : (
          <FormControl className="manage_order_filter">
            <span
              style={{
                fontSize: "14px",
                color: "#AFAFAF",
              }}
            >
              Filter:
            </span>
            <Select
              labelId="dropdown_label"
              value={statusFilter}
              size="small"
              sx={{ minWidth: "190px", marginLeft: "10px" }}
              onChange={(e) => {
                handleStatusFilterChange(e.target.value);
              }}
              renderValue={(selected) => {
                const item = orderTrackingStagesList.find(
                  (d) => d.id === selected
                );
                return item ? (
                  <div className="dropdown_selected_item">
                    {capitalize(item?.dmsLabel ?? "")}
                  </div>
                ) : null;
              }}
            >
              {orderTrackingStagesList.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <div className="dropdown_menu_item">
                    <span>{capitalize(item?.dmsLabel ?? "")}</span>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </div>

      {loader ? (
        <Loader sx={{ position: "absolute", top: "55%" }} />
      ) : (
        <CustomDataGrid
          rows={Array.isArray(rows) ? rows : []}
          isCheckboxSelection={true}
          columnsMetaData={managePrimaryOrderColumnsMetaData(showSiteId)}
          setCheckedIdArray={handleSetCheckedIdArray}
          orderStatusConfig={orderTrackingStagesList}
          redirectUrl="/primaryOms/manageOrders/viewOrder/"
          tableWidth={0.745}
          downloadSingleReportConfig={downloadSingleReportConfig}
          orderSourceConfig={orderSourcesRef.current}
          enableRowClickRedirect={true}
        />
      )}
    </div>
  )
};

export default ManagePrimaryOrder;
